<template>
  <transition leave-active-class="ds-modal-leave-active-component">
    <div
      v-show="toggleModal"
      ref="modalRef"
      :class="[
        `ds-modal ds-modal-${size}`,
        centerTransition ? 'ds-modal-center' : 'ds-modal-end',
      ]"
      data-testid="ds-modal"
    >
      <transition
        enter-active-class="ds-modal-enter-active"
        enter-from-class="ds-modal-enter-from"
        enter-to-class="ds-modal-enter-to"
        leave-active-class="ds-modal-leave-active"
        leave-from-class="ds-modal-leave-from"
        leave-to-class="ds-modal-leave-to"
      >
        <div
          v-if="toggleModal"
          ref="overlayRef"
          tabindex="-1"
          :class="['ds-modal-overlay', { 'cursor-pointer': !static }]"
          data-testid="ds-modal-overlay"
          @click="onClose"
          @keydown.esc="onClose"
        >
          <div class="ds-modal-overlay-bg" />
        </div>
      </transition>
      <transition
        enter-active-class="ds-modal-wrap-body-enter-active"
        :enter-from-class="
          centerTransition
            ? 'ds-modal-wrap-body-enter-from-center'
            : 'ds-modal-wrap-body-enter-from'
        "
        :enter-to-class="
          centerTransition
            ? 'ds-modal-wrap-body-enter-to-center'
            : 'ds-modal-wrap-body-enter-to'
        "
        leave-active-class="ds-modal-wrap-body-leave-active"
        :leave-from-class="
          centerTransition
            ? 'ds-modal-wrap-body-leave-from-center'
            : 'ds-modal-wrap-body-leave-from'
        "
        :leave-to-class="
          centerTransition
            ? 'ds-modal-wrap-body-leave-to-center'
            : 'ds-modal-wrap-body-leave-to'
        "
      >
        <div
          v-if="toggleModal"
          :id="id"
          ref="modalBodyRef"
          :data-testid="dataTestid"
          tabindex="1"
          class="ds-modal-wrap-body"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
          @keydown.esc="onClose"
        >
          <slot>
            <!--                        I'm the default body!-->
          </slot>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, provide, ref, watch } from "vue";
import { DsBrowser, DsModalHandler, DsScroll } from "../../helpers";
import { useDsModalStore } from "./DsModalStore";
import type { PropType } from "vue";

import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";

import type { DsThemeSize } from "../../types";

const emit = defineEmits(["opened", "closed", "beforeOpen", "beforeClose"]);
const modalRef = ref<HTMLElement | null>(null);
const overlayRef = ref<HTMLElement | null>(null);
const modalBodyRef = ref<HTMLDivElement | null>(null);
const elementToObserve = ref<HTMLElement | null>(null);

defineOptions({
  name: "DsModal",
});

const props = defineProps({
  id: {
    type: String,
    default: "modal",
  },
  dataTestid: {
    type: String,
    default: "ds-modal-wrap-body",
  },
  centerTransition: {
    type: Boolean,
    default: false,
  },
  static: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String as PropType<DsThemeSize>,
    default: "sm",
  },
  isForm: {
    type: Boolean,
    default: false,
  },
});

const toggleModal = computed((): boolean => {
  if (nbModals.value > 0) {
    if (useDsModalStore().modals[indexStoreModal.value]) {
      return useDsModalStore().modals[indexStoreModal.value].show;
    }
  }
  return false;
});

const modalId = computed((): string => {
  return modalBodyRef.value ? modalBodyRef.value.id : props.id;
});

const indexStoreModal = computed((): number => {
  return useDsModalStore().modals.findIndex(
    (modal) => modal.id === modalId.value
  );
});

const nbModals = computed((): number => {
  return useDsModalStore().modals.length;
});

const isMobile = computed((): boolean => {
  const breakpoints = useBreakpoints(breakpointsTailwind);
  return breakpoints.smaller("lg").value || DsBrowser.isMobile();
});

const isForm = computed((): boolean => props.isForm);

const close = (): void => {
  DsModalHandler.close(modalId.value);
};

function onClose(): void | (() => null) {
  return !props.static ? close() : () => null;
}

onBeforeUnmount(() => {
  setTimeout(() => {
    if (toggleModal.value) {
      showScroll();
    }
    unsubscribe();
  }, 200);
});

const unsubscribe = useDsModalStore().$onAction(({ name, args: modal }) => {
  if (name === "beforeClose" && modal[0] === props.id) {
    emit("beforeClose");
    showScroll();
  } else if (name === "beforeOpen" && modal[0] === props.id) {
    emit("beforeOpen");
  }
});

watch(isMobile, () => {
  if (toggleModal.value) {
    showScroll();
    hideScroll();
  }
});

watch(toggleModal, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    emit("closed");
    setTimeout(() => {
      showScroll();
    }, 200);
  } else {
    hideScroll();
    setTimeout(() => {
      emit("opened");
      if (!props.static) {
        overlayRef.value?.focus();
      }
    }, 300);
  }
});

function showScroll(): void {
  if (elementToObserve.value) {
    elementToObserve.value.removeAttribute("style");
  }
}

function hideScroll(): void {
  if (modalRef.value) {
    elementToObserve.value = DsScroll.getScrollableParent(modalRef.value);

    const scrollbarWidth =
      elementToObserve.value.offsetWidth - elementToObserve.value.scrollWidth;
    if (scrollbarWidth !== 0) {
      elementToObserve.value.style.paddingRight = `${scrollbarWidth}px`;
    }
    elementToObserve.value.style.overflow = "hidden";
  }
}

provide("modalId", modalId);
provide("isForm", isForm);

defineExpose({
  close,
  toggleModal,
  modalId,
});
</script>

<style lang="scss" scoped>
.ds-modal {
  @apply fixed inset-0 h-full z-50 p-4 flex m-0;

  &-center {
    @apply items-center justify-center;
  }

  &-end {
    @apply items-end sm:items-center sm:justify-center;
  }

  &-overlay {
    @apply fixed inset-0 transition-opacity focus:outline-0 focus:ring-0;

    &-bg {
      @apply absolute inset-0 bg-gray-900 bg-opacity-30 focus:outline-0 focus:ring-0;
    }
  }

  &-wrap {
    &-body {
      @apply z-10 flex flex-col w-full cursor-default focus:outline-0 focus:ring-0 transform overflow-hidden max-h-full rounded-2xl bg-white shadow-xl transition-all sm:max-w-[740px];

      &-enter {
        &-active {
          @apply duration-300 ease-out;
        }

        &-from {
          @apply opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95;

          &-center {
            @apply opacity-0 scale-95;
          }
        }

        &-to {
          @apply opacity-100 translate-y-0 sm:scale-100;

          &-center {
            @apply opacity-100 scale-100;
          }
        }
      }

      &-leave {
        &-active {
          @apply duration-200 ease-in;
        }
        &-from {
          @apply opacity-100 translate-y-0 sm:scale-100;
          &-center {
            @apply opacity-100 scale-100;
          }
        }
        &-to {
          @apply opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95;

          &-center {
            @apply opacity-0 scale-95;
          }
        }
      }
    }
  }

  &-xs {
    .ds-modal-wrap-body {
      @apply sm:max-w-[360px];
    }
  }
  &-sm {
    .ds-modal-wrap-body {
      @apply sm:max-w-[480px];
    }
  }
  &-md {
    .ds-modal-wrap-body {
      @apply sm:max-w-[600px];
    }
  }
  &-xl {
    .ds-modal-wrap-body {
      @apply sm:max-w-[1080px];
    }
  }
  &-xxl {
    .ds-modal-wrap-body {
      @apply sm:max-w-[1920px];
    }
  }

  &-enter {
    &-active {
      @apply duration-300 ease-out;
    }
    &-from {
      @apply opacity-0;
    }
    &-to {
      @apply opacity-100;
    }
  }

  &-leave {
    &-active {
      @apply duration-200 ease-in;

      &-component {
        @apply duration-200;
      }
    }

    &-from {
      @apply opacity-100;
    }

    &-to {
      @apply opacity-0;
    }
  }
}
</style>
